import axios from 'axios';
import { isFeatureEnabled } from '../jsx/components/Util/featureFlags';
import { sollecitiSimulati } from './impostazioniSistema';

/*
 * Certificati medici richiesti dalla struttura (docs/richieste-backend.md, capitolo 7).
 *
 * La struttura sceglie un tesserato e gli chiede il certificato: il player riceve email e notifica, e nel
 * web player accetta o rifiuta (con una motivazione facoltativa); la struttura riceve l'email con la risposta.
 *
 * SIMULAZIONE: finché il backend non ha le rotte, tesserati e richieste stanno nel localStorage di questo
 * browser. In simulazione la scheda permette anche di «rispondere come il player», per provare tutto il giro.
 * Con REACT_APP_CERTIFICATI_BACKEND=S si usano le rotte vere.
 */
export const CERTIFICATI_SIMULAZIONE = !isFeatureEnabled(process.env.REACT_APP_CERTIFICATI_BACKEND ?? 'N');

export const STATI_CERTIFICATO = {
    REQUESTED: { label: 'Richiesto', tono: 'non-attivo' },
    TO_VERIFY: { label: 'Da verificare', tono: 'non-attivo' },
    NOT_APPROVED: { label: 'Non approvato', tono: 'scaduto' },
    VALID: { label: 'Valido', tono: 'attivo' },
    EXPIRED: { label: 'Scaduto', tono: 'scaduto' },
    REFUSED: { label: 'Rifiutato', tono: 'scaduto' },
    NONE: { label: 'Mai richiesto', tono: 'bozza' },
};
export const etichettaStatoCertificato = (s) => STATI_CERTIFICATO[s]?.label || s || '';

export const ESITI_RICHIESTA = {
    PENDING: 'In attesa di risposta',
    ACCEPTED: 'Accettata',
    REFUSED: 'Rifiutata',
    CANCELLED: 'Annullata',
};

const CHIAVE = 'sid-certificati-medici-simulazione-v3';
const oggi = () => new Date().toISOString();
const traGiorni = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const giorniFa = (n) => new Date(Date.now() - n * 86400000).toISOString();

// Tesserati di prova: alcuni con certificato valido, scaduto, richiesto, rifiutato, mai chiesto
const ESEMPIO = () => ({
    tesserati: [
        { personId: 'p1', personType: 'PLAYER', name: 'Giulia Bianchi', email: 'giulia.bianchi@example.com', sportType: 'TENNIS', certificato: { expiration: traGiorni(210), documentUrl: '' } },
        { personId: 'p2', personType: 'PLAYER', name: 'Marco Verdi', email: 'marco.verdi@example.com', sportType: 'CALCIO', certificato: { expiration: traGiorni(-20), documentUrl: '' } },
        { personId: 'p3', personType: 'PLAYER', name: 'Sara Neri', email: 'sara.neri@example.com', sportType: 'PALLAVOLO', certificato: null },
        { personId: 'j1', personType: 'JUNIOR', name: 'Luca Rossi', email: 'mario.rossi@example.com', sportType: 'CALCIO', certificato: null },
        { personId: 'p4', personType: 'PLAYER', name: 'Elena Gallo', email: 'elena.gallo@example.com', sportType: 'TENNIS', certificato: { expiration: traGiorni(45), documentUrl: '' } },
        { personId: 'p5', personType: 'PLAYER', name: 'Paolo Conti', email: 'paolo.conti@example.com', sportType: 'NUOTO', certificato: null },
        { personId: 'j2', personType: 'JUNIOR', name: 'Sofia Greco', email: 'anna.greco@example.com', sportType: 'NUOTO', certificato: null },
        { personId: 'p6', personType: 'PLAYER', name: 'Andrea Ferri', email: 'andrea.ferri@example.com', sportType: 'PADEL', certificato: null },
    ],
    richieste: [
        { id: 'r1', personId: 'p1', status: 'ACCEPTED', message: '', reason: '', createdAt: giorniFa(40), respondedAt: giorniFa(39), review: { status: 'APPROVED', reason: '', at: giorniFa(38) } },
        { id: 'r2', personId: 'p2', status: 'ACCEPTED', message: '', reason: '', createdAt: giorniFa(400), respondedAt: giorniFa(399), review: { status: 'APPROVED', reason: '', at: giorniFa(398) } },
        { id: 'r3', personId: 'p3', status: 'PENDING', message: 'Ci serve per il torneo di novembre.', reason: '', createdAt: giorniFa(16), respondedAt: null, solleciti: [giorniFa(10)] },
        { id: 'r4', personId: 'j1', status: 'PENDING', message: '', reason: '', createdAt: giorniFa(1), respondedAt: null },
        { id: 'r5', personId: 'p4', status: 'ACCEPTED', message: '', reason: '', createdAt: giorniFa(10), respondedAt: giorniFa(9), review: { status: 'PENDING', reason: '', at: null } },
        { id: 'r6', personId: 'p5', status: 'REFUSED', message: '', reason: 'Lo consegno a mano in segreteria la prossima settimana.', createdAt: giorniFa(12), respondedAt: giorniFa(3) },
    ],
});

const leggi = () => {
    try {
        const d = JSON.parse(localStorage.getItem(CHIAVE) || 'null');
        if (d && Array.isArray(d.tesserati) && Array.isArray(d.richieste)) return d;
    } catch { /* storage non disponibile */ }
    const d = ESEMPIO();
    scrivi(d);
    return d;
};
const scrivi = (d) => {
    try { localStorage.setItem(CHIAVE, JSON.stringify(d)); } catch { /* storage non disponibile */ }
};

/** Lo stato del certificato come lo calcola il backend (capitolo 7). */
const statoDi = (tesserato, richiesta) => {
    const scadenza = (c) => (c.expiration >= new Date().toISOString().slice(0, 10) ? 'VALID' : 'EXPIRED');
    // Caricato a mano dalla struttura: vale subito, senza richiesta (o se è più recente della richiesta)
    const caricato = tesserato.certificato?.uploadedByClub ? tesserato.certificato : null;
    if (caricato && (!richiesta || String(caricato.uploadedAt) >= String(richiesta.createdAt))) return scadenza(caricato);
    if (!richiesta || richiesta.status === 'CANCELLED') return 'NONE';
    if (richiesta.status === 'PENDING') return 'REQUESTED';
    if (richiesta.status === 'REFUSED') return 'REFUSED';
    if (!tesserato.certificato) return 'REQUESTED';
    // Il certificato mandato dal player va verificato e approvato dalla struttura
    const verifica = richiesta.review?.status || 'PENDING';
    if (verifica === 'PENDING') return 'TO_VERIFY';
    if (verifica === 'REJECTED') return 'NOT_APPROVED';
    return scadenza(tesserato.certificato);
};

/**
 * Solleciti di una richiesta (simulazione del backend): gli automatici cadono dopo i giorni impostati dal
 * super admin (Impostazioni › Certificati medici) finché il player non risponde; i manuali sono salvati.
 * Nel backend arrivano già pronti in `request.reminders` (capitolo 8).
 */
const sollecitiDi = (r) => {
    if (r.status === 'CANCELLED' && !r.cancelledAt) return [];
    const giorni = sollecitiSimulati();
    const fine = r.respondedAt || r.cancelledAt || new Date().toISOString();
    const auto = [giorni.firstReminderDays, giorni.secondReminderDays, giorni.thirdReminderDays]
        .map((n, i) => ({ sentAt: new Date(new Date(r.createdAt).getTime() + Number(n) * 86400000).toISOString(), type: 'AUTO', number: i + 1 }))
        .filter((x) => x.sentAt <= fine);
    const manuali = (r.solleciti || []).map((at) => ({ sentAt: at, type: 'MANUAL' }));
    return [...auto, ...manuali].sort((a, b) => a.sentAt.localeCompare(b.sentAt));
};

const riga = (d, t) => {
    const tutte = d.richieste.filter((r) => r.personId === t.personId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((r) => ({ ...r, sportType: r.sportType || t.sportType, reminders: sollecitiDi(r) }));
    const richiesta = tutte[0] || null;
    const stato = statoDi(t, richiesta);
    const caricato = !!t.certificato?.uploadedByClub;
    const accettata = caricato || (richiesta && richiesta.status === 'ACCEPTED');
    return {
        personId: t.personId,
        personType: t.personType,
        name: t.name,
        email: t.email,
        sportType: t.sportType,
        certificateStatus: stato,
        expiration: accettata && t.certificato ? t.certificato.expiration : null,
        documentUrl: accettata && t.certificato ? t.certificato.documentUrl : null,
        documentName: accettata && t.certificato ? t.certificato.documentName || '' : '',
        uploadedByClub: caricato,
        uploadedAt: caricato ? t.certificato.uploadedAt : null,
        request: richiesta,
        requests: tutte,
    };
};

const token = () => {
    try { return JSON.parse(sessionStorage.getItem('userDetails')).idToken; } catch { return ''; }
};
const basePath = () => String(process.env.REACT_APP_API_BASE_PATH || '').replace(/\/+$/, '');
const chiama = async (method, path, data) => {
    try {
        const r = await axios.request({ method, url: `${basePath()}${path}`, data, headers: { authorization: token(), accept: 'application/json', 'Content-Type': 'application/json' } });
        return r.data;
    } catch (e) {
        return { error: e.response?.data?.detail || e.response?.data?.message || 'Errore generico', status: e.response?.status };
    }
};

/** 7.1 — un elemento per tesserato, con lo stato del certificato e l'ultima richiesta. */
export const elencoCertificati = async () => {
    if (CERTIFICATI_SIMULAZIONE) {
        const d = leggi();
        return d.tesserati.map((t) => riga(d, t));
    }
    const r = await chiama('get', '/profile/club/medicalCertificates');
    return r?.error ? r : (r.items || r);
};

export const leggiCertificato = async (personId) => {
    const elenco = await elencoCertificati();
    if (elenco?.error) return elenco;
    return elenco.find((e) => e.personId === personId) || { error: 'Tesserato non trovato.', status: 404 };
};

/** 7.2 — invia la richiesta: email al player e notifica nell'app (lo fa il backend). */
export const richiediCertificato = async ({ personId, personType, sportType, message }) => {
    const dati = { personId, personType, sportType: sportType || undefined, message: String(message || '').trim() || undefined };
    if (CERTIFICATI_SIMULAZIONE) {
        const d = leggi();
        const t = d.tesserati.find((x) => x.personId === personId);
        if (!t) return { error: 'Tesserato non trovato.', status: 404 };
        if (d.richieste.some((r) => r.personId === personId && r.status === 'PENDING')) {
            return { error: `C'è già una richiesta in attesa per «${t.name}».`, status: 409 };
        }
        const nuova = { id: `r-${Date.now().toString(36)}`, personId, status: 'PENDING', message: dati.message || '', reason: '', createdAt: oggi(), respondedAt: null };
        d.richieste.push(nuova);
        scrivi(d);
        return nuova;
    }
    return chiama('post', '/profile/club/medicalCertificateRequests', dati);
};

/** Si elimina una richiesta che non è finita bene: in attesa, rifiutata, da verificare o non approvata. */
export const richiestaEliminabile = (r) => !!r && ['PENDING', 'REFUSED', 'ACCEPTED'].includes(r.status) && !(r.status === 'ACCEPTED' && r.review?.status === 'APPROVED');

/** 7.3 — la struttura elimina la richiesta (resta nella storia come annullata): il player non la vede più. */
export const annullaRichiesta = async (id) => {
    if (CERTIFICATI_SIMULAZIONE) {
        const d = leggi();
        const r = d.richieste.find((x) => x.id === id);
        if (!r) return { error: 'Richiesta non trovata.', status: 404 };
        if (!richiestaEliminabile(r)) return { error: 'Il certificato è approvato: la richiesta non si può eliminare.', status: 409 };
        r.status = 'CANCELLED';
        r.cancelledAt = oggi();
        scrivi(d);
        return {};
    }
    return chiama('delete', `/profile/club/medicalCertificateRequests/${encodeURIComponent(id)}`);
};

/** 7.4 — sollecito: rimanda email e notifica. */
export const sollecitaRichiesta = async (id) => {
    if (CERTIFICATI_SIMULAZIONE) {
        const d = leggi();
        const r = d.richieste.find((x) => x.id === id);
        if (!r || r.status !== 'PENDING') return { error: 'Nessuna richiesta in attesa.' };
        const ultimo = (r.solleciti || []).slice(-1)[0];
        if (ultimo && Date.now() - new Date(ultimo).getTime() < 86400000) {
            return { error: 'Hai già sollecitato nelle ultime 24 ore: puoi rifarlo domani.', status: 429 };
        }
        r.solleciti = [...(r.solleciti || []), oggi()];
        scrivi(d);
        return {};
    }
    return chiama('post', `/profile/club/medicalCertificateRequests/${encodeURIComponent(id)}/remind`);
};

/**
 * Solo simulazione: la risposta del player (nel sistema vero arriva dal web player, rotte 7.6 e 7.7).
 * Accettando, se il tesserato non ha un certificato se ne crea uno di prova valido un anno.
 */
export const simulaRispostaPlayer = async (id, accetta, reason) => {
    const d = leggi();
    const r = d.richieste.find((x) => x.id === id);
    if (!r || r.status !== 'PENDING') return { error: 'Nessuna richiesta in attesa.' };
    r.status = accetta ? 'ACCEPTED' : 'REFUSED';
    r.reason = String(reason || '').trim();
    r.respondedAt = oggi();
    const t = d.tesserati.find((x) => x.personId === r.personId);
    if (accetta && t && !t.certificato) t.certificato = { expiration: traGiorni(365), documentUrl: '', documentName: 'certificato-medico.pdf' };
    if (accetta) r.review = { status: 'PENDING', reason: '', at: null };
    scrivi(d);
    return {};
};

/** Il file in base64 (senza il prefisso data:), come lo manda il web player in POST /profile/medicalCertificates. */
const leggiFile = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('File non leggibile'));
    r.readAsDataURL(file);
});

// Il file viaggia in base64 dentro la richiesta (+33%) e il backend su Lambda non accetta più di 6 MB:
// le foto si riducono qui (lato lungo 2400 px, JPEG), i PDF devono stare entro 3 MB. Come nel web player.
const MAX_FILE = 3 * 1024 * 1024;
const RIDUCI_DA = 1024 * 1024;
const LATO_MAX = 2400;
const riduciFoto = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image')); };
    img.onload = () => {
        URL.revokeObjectURL(url);
        const k = Math.min(1, LATO_MAX / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * k);
        canvas.height = Math.round(img.naturalHeight * k);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; // PNG trasparenti: fondo bianco invece di nero
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => (b ? resolve(new File([b], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' })) : reject(new Error('canvas'))), 'image/jpeg', 0.85);
    };
    img.src = url;
});

/**
 * 7.8 — la struttura carica a mano il certificato di un tesserato (PDF o foto, con la scadenza): vale subito.
 * In simulazione il file resta nel browser solo se è piccolo (fino a 1,5 MB), per poterlo riaprire.
 */
export const caricaCertificato = async ({ personId, personType, sportType, expiration, file }) => {
    if (!file) return { error: 'Scegli il file del certificato (PDF o foto).' };
    if (!(/^image\//.test(file.type) || file.type === 'application/pdf' || /\.pdf$/i.test(file.name))) return { error: 'Il certificato deve essere un PDF o una foto.' };
    if (!expiration) return { error: 'Indica la data di scadenza del certificato.' };
    const foto = /^image\//.test(file.type);
    if (foto && file.size > RIDUCI_DA) {
        // Se il browser non sa leggere la foto (es. HEIC fuori da Safari) si tiene l'originale
        const ridotta = await riduciFoto(file).catch(() => null);
        if (ridotta && ridotta.size < file.size) file = ridotta;
    }
    if (file.size > MAX_FILE) {
        return { error: foto
            ? 'La foto è troppo grande anche dopo la riduzione: scegline una più piccola (al massimo 3 MB).'
            : 'Il PDF supera 3 MB: riducilo oppure carica una foto del certificato.' };
    }
    let dataUrl;
    try { dataUrl = await leggiFile(file); } catch { return { error: 'Non riesco a leggere il file.' }; }
    if (CERTIFICATI_SIMULAZIONE) {
        const d = leggi();
        const t = d.tesserati.find((x) => x.personId === personId);
        if (!t) return { error: 'Tesserato non trovato.', status: 404 };
        t.certificato = {
            expiration,
            documentUrl: file.size <= 1.5 * 1024 * 1024 ? dataUrl : '',
            documentName: file.name,
            uploadedByClub: true,
            uploadedAt: oggi(),
        };
        scrivi(d);
        return {};
    }
    return chiama('post', '/profile/club/medicalCertificates', {
        personId, personType, sportType: sportType || undefined, expiration,
        document: { content: dataUrl.split(',')[1], name: file.name },
    });
};

/**
 * 7.9 / 7.10 — verifica del certificato mandato dal player: la struttura lo approva (diventa Valido o Scaduto
 * secondo la scadenza) oppure non lo approva, con il motivo: il player riceve email e notifica e ne carica un altro.
 */
export const verificaCertificato = async (id, approva, reason) => {
    const motivo = String(reason || '').trim();
    if (!approva && !motivo) return { error: 'Scrivi il motivo: il player lo legge per caricare il certificato giusto.' };
    if (CERTIFICATI_SIMULAZIONE) {
        const d = leggi();
        const r = d.richieste.find((x) => x.id === id);
        if (!r || r.status !== 'ACCEPTED') return { error: 'Nessun certificato da verificare.' };
        r.review = { status: approva ? 'APPROVED' : 'REJECTED', reason: motivo, at: oggi() };
        scrivi(d);
        return {};
    }
    return chiama('post', `/profile/club/medicalCertificateRequests/${encodeURIComponent(id)}/${approva ? 'approve' : 'reject'}`, approva ? {} : { reason: motivo });
};
